/* eslint-disable react/jsx-props-no-spreading */
import { useCallback, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { SNIPPET_DETECTION, VENDORED_DEPENDENCY_DETECTION } from '../../../../../shared/featureFlags/flagNames';
import { Category, IssueScopeKind } from '../../../../../shared/issuesV2';
import { window } from '../../../../globals';
import { FeatureFlagRequired } from '../../../generic';
import LabeledCheckbox from '../../../generic/Forms/LabeledCheckbox';
import { SecurityRequired } from '../../../generic/Gates';
import IssuesSection from '../../../shared/settings/SettingsPageIssueSection';
import Section from '../../../shared/settings/SettingsPageSection';

import PropagateModal from './PropagateIssuesModal';
import useIssueSettings from './useIssueSettings';
import type { IssueSettingsProps } from './types';
import type { GenericIssueSettingsFields } from './utils';

/**
 * SettingsOrganizationProjectIssuesSecurity is the settings page for
 * managing the default security issue settings for projects.
 */
const SettingsOrganizationProjectIssuesSecurity: VoidFunctionComponent<IssueSettingsProps> = ({
  policies: policyOptions,
}) => {
  const [isPropagateModalOpen, setIsPropagateModalOpen] = useState<boolean>(false);
  const {
    settings,
    isLoading,
    onUpdateSettings,
    policies,
    filters,
    isSaveButtonDisabled,
    onSaveSettings,
  } = useIssueSettings(
    window.user?.organizationId as number,
    Category.Vulnerability,
    policyOptions,
  );
  const securitySettings = settings;

  const renderSnippetPropagateSettings = useCallback(
    ({ additionalFields, setAdditionalFields }) => {
      return (
        <FeatureFlagRequired flagName={SNIPPET_DETECTION}>
          <LabeledCheckbox
            label={
            securitySettings?.isSnippetScanningEnabled
              ? `Enable snippet issue scanning for all projects`
              : `Disable snippet issue scanning for all projects`
          }
            isChecked={additionalFields.includes('projectDefaultSnippetSecurityIssueScanningEnabled')}
            onChange={() =>
              setAdditionalFields((prev: GenericIssueSettingsFields[]) =>
                prev.includes('projectDefaultSnippetSecurityIssueScanningEnabled')
                  ? prev.filter((field) => field !== 'projectDefaultSnippetSecurityIssueScanningEnabled')
                  : [...prev, 'projectDefaultSnippetSecurityIssueScanningEnabled'],
              )}
            isRippleDisabled
          />
        </FeatureFlagRequired>
      );
    }, [securitySettings]);

  const renderVendoredPropagateSettings = useCallback(
    ({ additionalFields, setAdditionalFields }) => {
      return (
        <FeatureFlagRequired flagName={VENDORED_DEPENDENCY_DETECTION}>
          <LabeledCheckbox
            label={
            securitySettings?.isVendoredScanningEnabled
              ? `Enable vendored dependency issue scanning for all projects`
              : `Disable vendored dependency issue scanning for all projects`
          }
            isChecked={additionalFields.includes('projectDefaultVendoredSecurityIssueScanningEnabled')}
            onChange={() =>
              setAdditionalFields((prev: GenericIssueSettingsFields[]) =>
                prev.includes('projectDefaultVendoredSecurityIssueScanningEnabled')
                  ? prev.filter((field) => field !== 'projectDefaultVendoredSecurityIssueScanningEnabled')
                  : [...prev, 'projectDefaultVendoredSecurityIssueScanningEnabled'],
              )}
            isRippleDisabled
          />
        </FeatureFlagRequired>
      );
    }, [securitySettings]);

  return (
    <>
      <Section>
        <Section.Left heading="Security">
          <Section.Paragraph>Default configuration for security issues on new projects</Section.Paragraph>
          <Section.Buttons>
            <Section.PropagateButton onClick={() => setIsPropagateModalOpen(true)} isDisabled={!isSaveButtonDisabled} />
            <Section.SaveButton onClick={onSaveSettings} isLoading={isLoading.save} isDisabled={isSaveButtonDisabled} />
          </Section.Buttons>
        </Section.Left>
        <Section.Right>
          <IssuesSection
            category={Category.Vulnerability}
            scopeType={IssueScopeKind.Global}
            isScanningEnabled={settings?.isScanningEnabled || false}
            onToggleIsScanningEnabled={(isScanningEnabled) => onUpdateSettings({
              isScanningEnabled,
              ...(!isScanningEnabled && { isSnippetScanningEnabled: false, isVendoredScanningEnabled: false }),
            })}
            isCICheckEnabled={settings?.isCICheckEnabled || false}
            onToggleIsCICheckEnabled={(isCICheckEnabled) => onUpdateSettings({ isCICheckEnabled })}
            policyOptions={policies.policyOptions}
            selectedPolicyOption={policies.selectedPolicyOption}
            onSelectedPolicyChange={(policyId) => onUpdateSettings({ policyId })}
            filterOptions={filters.filterOptions}
            selectedFilterOption={filters.selectedFilterOption}
            onSelectedFilterChange={(filterId) => onUpdateSettings({ filterId })}
            // Users with organization settings permissions can edit the issue policy and filters
            canEditFilters
            isSnippetScanningEnabled={securitySettings?.isSnippetScanningEnabled ?? false}
            onToggleSnippetScanning={(isChecked) => onUpdateSettings({ isSnippetScanningEnabled: isChecked })}
            isVendoredScanningEnabled={securitySettings?.isVendoredScanningEnabled ?? false}
            onToggleVendoredScanning={(isChecked) => onUpdateSettings({ isVendoredScanningEnabled: isChecked })}
            canEditPolicy
          />
        </Section.Right>
      </Section>
      {settings && (
        <PropagateModal
          category={Category.Vulnerability}
          isOpen={isPropagateModalOpen}
          onHide={() => setIsPropagateModalOpen(false)}
          policyName={policies.selectedPolicyName}
          filterName={filters.selectedFilterName}
          settings={settings}
          renderSnippetSettings={renderSnippetPropagateSettings}
          renderVendoredSettings={renderVendoredPropagateSettings}
        />
      )}
    </>
  );
};

/**
 * Container which gates the Security settings page behind the
 * SecurityRequired component, which checks `organization.security_enabled`
 */
const SettingsOrganizationProjectIssuesSecurityGate: VoidFunctionComponent<IssueSettingsProps> = (props) => (
  <SecurityRequired>
    <SettingsOrganizationProjectIssuesSecurity {...props} />
  </SecurityRequired>
);

export default SettingsOrganizationProjectIssuesSecurityGate;
